import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { newTasksApi, newTaskKeys, newTaskTypesApi, newTaskTypeKeys } from '../api';
import type { TaskFilters, TaskStatus, CreateTaskDto, UpdateTaskDto, CompleteAdjustmentDto, CreateTaskTypeDto, UpdateTaskTypeDto } from '../types';

// Tasks hooks
export function useNewTasks(filters: TaskFilters) {
  return useQuery({
    queryKey: newTaskKeys.list(filters),
    queryFn: () => newTasksApi.getAll(filters),
  });
}

export function useNewTask(id: string | number | undefined) {
  return useQuery({
    queryKey: newTaskKeys.detail(id ?? ''),
    queryFn: () => newTasksApi.getById(id!),
    enabled: !!id,
  });
}

export function useNewTaskMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateTaskDto) => newTasksApi.create(data),
    onSuccess: () => {
      toast.success(t('taskCreatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingTask')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateTaskDto }) =>
      newTasksApi.update(id, data),
    onSuccess: () => {
      toast.success(t('taskUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingTask')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => newTasksApi.delete(id),
    onSuccess: () => {
      toast.success(t('taskDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingTask')));
    },
  });

  const reassignStaffMutation = useMutation({
    mutationFn: ({ id, staffId }: { id: string | number; staffId: number }) =>
      newTasksApi.reassignStaff(id, staffId),
    onSuccess: () => {
      toast.success(t('staffReassignedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorReassigningStaff')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    reassignStaffMutation,
  };
}

export function useNewTaskStatusCount(filters: TaskFilters) {
  return useQuery({
    queryKey: newTaskKeys.statusCount(filters),
    queryFn: () => newTasksApi.getStatusCount(filters),
  });
}

export function useNewTaskStatusMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const changeStatusMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: { newStatus: TaskStatus; comment?: string; result?: string; isChangeRequest?: boolean; dueAt?: string } }) =>
      newTasksApi.changeStatus(id, data),
    onSuccess: () => {
      toast.success(t('taskStatusChangedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorChangingTaskStatus')));
    },
  });

  const adjustMutation = useMutation({
    mutationFn: (id: string | number) => newTasksApi.adjust(id),
    onSuccess: () => {
      toast.success(t('adjustment.createdSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
    },
    onError: (error: unknown) => {
      const msg = getApiErrorMessage(error, t('adjustment.createError'));
      // 409 = already has an adjustment; surface a specific message
      const isConflict = msg.includes('409') || msg.toLowerCase().includes('conflict') || msg.toLowerCase().includes('already');
      toast.error(isConflict ? t('adjustment.alreadyExists') : msg);
    },
  });

  const completeAdjustmentMutation = useMutation({
    mutationFn: ({
      adjustmentId,
      data,
    }: {
      adjustmentId: string | number;
      data: CompleteAdjustmentDto;
    }) => newTasksApi.completeAdjustment(adjustmentId, data),
    onSuccess: () => {
      toast.success(t('adjustment.finishedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('adjustment.finishError')));
    },
  });

  const deleteAdjustmentMutation = useMutation({
    mutationFn: (adjustmentId: string | number) =>
      newTasksApi.deleteAdjustment(adjustmentId),
    onSuccess: () => {
      toast.success(t('adjustment.deletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('adjustment.deleteError')));
    },
  });

  return {
    changeStatusMutation,
    adjustMutation,
    completeAdjustmentMutation,
    deleteAdjustmentMutation,
  };
}

export function useTaskAdjustments(taskId: string | number | undefined) {
  return useQuery({
    queryKey: newTaskKeys.adjustments(taskId ?? ''),
    queryFn: () => newTasksApi.getAdjustments(taskId!),
    enabled: !!taskId,
  });
}

// Task Types hooks
export function useNewTaskTypes(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: newTaskTypeKeys.list(params),
    queryFn: () => newTaskTypesApi.getAll(params),
  });
}

export function useNewTaskType(id: string | number | undefined) {
  return useQuery({
    queryKey: newTaskTypeKeys.detail(id ?? ''),
    queryFn: () => newTaskTypesApi.getById(id!),
    enabled: !!id,
  });
}

export function useNewTaskTypeMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateTaskTypeDto) => newTaskTypesApi.create(data),
    onSuccess: () => {
      toast.success(t('taskTypeCreatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskTypeKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingTaskType')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateTaskTypeDto }) =>
      newTaskTypesApi.update(id, data),
    onSuccess: () => {
      toast.success(t('taskTypeUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskTypeKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingTaskType')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => newTaskTypesApi.delete(id),
    onSuccess: () => {
      toast.success(t('taskTypeDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: newTaskTypeKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingTaskType')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
